/**
 * Web-Push subscription client. Registers the service worker,
 * fetches the VAPID public key from backend, and subscribes the browser.
 */

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

const API_ORIGIN = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL;

export function isPushSupported() {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

let swRegPromise = null;
export function registerServiceWorker() {
  if (!isPushSupported()) return Promise.resolve(null);
  if (!swRegPromise) {
    swRegPromise = navigator.serviceWorker.register('/service-worker.js', { scope: '/', updateViaCache: 'none' })
      .catch((e) => { console.warn('SW register failed', e); return null; });
  }
  return swRegPromise;
}

async function fetchVapidPublicKey() {
  const res = await fetch(`${API_ORIGIN}/api/push/vapid-public-key`);
  if (!res.ok) throw new Error('vapid key fetch failed');
  const d = await res.json();
  return d.publicKey;
}

/** Ensure permission + subscribe on server. Returns true on success. */
export async function subscribeToPush(patientId) {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  const reg = await registerServiceWorker();
  if (!reg) return { ok: false, reason: 'sw-failed' };

  if (Notification.permission === 'default') {
    const p = await Notification.requestPermission();
    if (p !== 'granted') return { ok: false, reason: p };
  } else if (Notification.permission !== 'granted') {
    return { ok: false, reason: Notification.permission };
  }

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const pubKey = await fetchVapidPublicKey();
    if (!pubKey) return { ok: false, reason: 'no-vapid' };
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(pubKey),
      });
    } catch (e) {
      console.warn('push subscribe failed', e);
      return { ok: false, reason: 'subscribe-failed' };
    }
  }

  // Post to backend
  const raw = sub.toJSON();
  const res = await fetch(`${API_ORIGIN}/api/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ patient_id: patientId, subscription: { endpoint: raw.endpoint, keys: raw.keys } }),
  });
  if (!res.ok) return { ok: false, reason: 'backend-reject' };
  return { ok: true };
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return { ok: false };
  const reg = await registerServiceWorker();
  const sub = reg && (await reg.pushManager.getSubscription());
  if (!sub) return { ok: true };
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  await fetch(`${API_ORIGIN}/api/push/unsubscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  });
  return { ok: true };
}

export async function firePushTest(patientId) {
  const res = await fetch(`${API_ORIGIN}/api/push/test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ patient_id: patientId }),
  });
  return res.ok ? res.json() : { sent: 0, total: 0 };
}
